import { MatchInfo } from '@/modules/ticker/types';
import { PlacementInterface, ZONE_IDS_PB } from '@/types/ads.types';
import { Subcategory } from '@/types/blog.types';
import { FreestarPBPlacementId } from '@/types/freestar.pb.types';
import { WidgetMLPLeaderboard } from '@/types/mlpLeaderboard.types';
import { WidgetPlayersRankingsData, WidgetPlayersRankingsV2Data } from '@/types/ranking.types';
import { RenderExternalNewsInterface } from '@/utils/actions/homepage/getExternalNews';
import { RenderNewsInterface } from '@/utils/actions/homepage/getNews';
import { RenderSideNewsInterface } from '@/utils/actions/homepage/getNewsByUuids';

export interface EditorState {
  structure: ColumnInterface[];
  lastUpdatedStructure?: ColumnInterface[];
  selectedWidgetId?: string;
  editingWidget?: ColumnWidgetType;
  insertingColumnIndex?: 0 | 1 | 2;
  isSaving?: boolean;
  successfullySaved?: boolean;
  error?: string;
  hasError?: boolean;
}

export enum BlockWidgetTypes {
  NEWS = 'NEWS', // NewsInterface has its own newsType property which can be 'format-a' (NewsCard) or 'format-b' (SmallNewsCard)
  VIDEO = 'VIDEO', // This is a NewsCard with optional property 'video' set to true
  IMAGE = 'IMAGE', // This is an ImageCard
  EXTERNAL_NEWS = 'EXTERNAL_NEWS', // ExternalNewsInterface has its own newsType property which can be 'format-a' (NewsCard) or 'format-b' (SmallNewsCard)
  MATCH = 'MATCH', // This is a MatchCardsContainer
  RESULT = 'RESULT', // This is a ResultCard
  CAROUSEL = 'CAROUSEL', // This is a Carousel
  MATCHV3 = 'MATCHV3', // This is a MatchV3Card
  ALERT = 'ALERT', // This is an Alert component
}

export interface SidebarWidgetInterface {
  area: 'CONTENT_AREA' | 'SIDEBAR'; // this represents area where this widget can be dropped (SIDEBAR or CONTENT_AREA)
  label: string;
  type: ColumnWidgetTypes; // this represents the type of secondary-sidebar widget (only column widgets can be dropped to columns)
}

export enum ColumnTypes {
  SIDEBAR_1 = 'SIDBAR_1_HOME_PAGE_COLUMN',
  CONTENT_AREA = 'CONTENT_AREA_HOME_PAGE_COLUMN',
  SIDEBAR_2 = 'SIDBAR_2_HOME_PAGE_COLUMN',
}

export enum ColumnWidgetTypes {
  BLOCK = 'BLOCK',
  NEWS = 'NEWS',
  VIDEO = 'VIDEO',
  IMAGE = 'IMAGE',
  EXTERNAL_NEWS = 'EXTERNAL_NEWS',
  COMPANY_WEBSITES = 'COMPANY_WEBSITES',
  FEATURED_PARTNERS = 'FEATURED_PARTNERS',
  HEADLINES = 'HEADLINES',
  RANKINGS = 'RANKINGS',
  RANKINGS_V2 = 'RANKINGS_V2',
  SIDE_NEWS = 'SIDE_NEWS',
  SOCIAL_MEDIA = 'SOCIAL_MEDIA',
  SIDE_ADVERTISEMENT = 'SIDE_ADVERTISEMENT',
  MIDDLE_ADVERTISEMENT = 'MIDDLE_ADVERTISEMENT',
  CAROUSEL = 'CAROUSEL',
  FEATURED_PLAYERS = 'FEATURED_PLAYERS',
  ALERT = 'ALERT',
  MLP_LEADERBOARD = 'MLP_LEADERBOARD',
  NEWSLETTER = 'NEWSLETTER',
}

// This interface represents a column
export interface ColumnInterface {
  type: ColumnTypes;
  widgets: ColumnWidgetType[];
}

// Widgets with one of these types can go inside a column
export type ColumnWidgetType =
  | BlockInterface
  | NewsWidgetInterface
  | VideoWidgetInterface
  | ImageWidgetInterface
  | ExternalNewsWidgetInterface
  | CompanyWebsitesInterface
  | FeaturedPartnersInterface
  | HeadlinesInterface
  | RankingsInterface
  | RankingsV2Interface
  | MLPLeaderboardInterface
  | SideNewsInterface
  | FeaturedPlayersInterface
  | SocialMediaInterface
  | SideAdvertisementInterface
  | MiddleAdvertisementInterface
  | CarouselWidgetInterface
  | AlertWidgetInterface
  | NewsletterWidgetInterface;

// Base interface for widgets
export interface BaseWidgetInterface {
  homepageId: number;
  displayOrder: number;
  uuid: string;
  dateCreated?: Date | string;
  dateDisplayFrom?: Date | string;
  dateDisplayTo?: Date | string;
  createdByFullName?: string;
  updateByFullName?: string;
  dateUpdated?: Date | string;
  dateArchived?: Date | string;
}
// Represents block widget that can be inside a column (widget with BLOCK type)
export interface BlockInterface extends BaseWidgetInterface {
  column: ColumnTypes.CONTENT_AREA;
  type: ColumnWidgetTypes.BLOCK;
  metadata: BlockMetadataInterace;
}

// Represents news widget that can be inside a column (widget with NEWS type)
export interface NewsWidgetInterface extends BaseWidgetInterface {
  column: ColumnTypes.CONTENT_AREA;
  type: ColumnWidgetTypes.NEWS;
  metadata: NewsInterface;
}

// Represents video widget that can be inside a column (widget with VIDEO type)
export interface VideoWidgetInterface extends BaseWidgetInterface {
  column: ColumnTypes.CONTENT_AREA;
  type: ColumnWidgetTypes.VIDEO;
  metadata: VideoInterface;
}

// Represents image widget that can be inside a column (widget with IMAGE type)
export interface ImageWidgetInterface extends BaseWidgetInterface {
  column: ColumnTypes.CONTENT_AREA;
  type: ColumnWidgetTypes.IMAGE;
  metadata: ImageInterface;
}

// Represents external news widget that can be inside a column (widget with EXTERNAL_NEWS type)
export interface ExternalNewsWidgetInterface extends BaseWidgetInterface {
  column: ColumnTypes.CONTENT_AREA;
  type: ColumnWidgetTypes.EXTERNAL_NEWS;
  metadata: ExternalNewsInterface;
}

// Represents company websites widget that can be inside a column (widget with COMPANY_WEBSITES type)
export interface CompanyWebsitesInterface extends BaseWidgetInterface {
  column: ColumnTypes.SIDEBAR_1 | ColumnTypes.SIDEBAR_2;
  type: ColumnWidgetTypes.COMPANY_WEBSITES;
  metadata: CompanyWebsitesMetadataInterface;
}

// Represents featured partners widget that can be inside a column (widget with FEATURED_PARTNERS type)
export interface FeaturedPartnersInterface extends BaseWidgetInterface {
  column: ColumnTypes.SIDEBAR_1 | ColumnTypes.SIDEBAR_2;
  type: ColumnWidgetTypes.FEATURED_PARTNERS;
  metadata: FeaturedPartnersMetadataInterface;
}

// Represents headlines widget that can be inside a column (widget with HEADLINES type)
export interface HeadlinesInterface extends BaseWidgetInterface {
  column: ColumnTypes.SIDEBAR_1 | ColumnTypes.SIDEBAR_2;
  type: ColumnWidgetTypes.HEADLINES;
  metadata: HeadlinesMetadataInterface;
}

// Represents rankings widget that can be inside a column (widget with RANKINGS type)
export interface RankingsInterface extends BaseWidgetInterface {
  column: ColumnTypes.SIDEBAR_1 | ColumnTypes.SIDEBAR_2;
  type: ColumnWidgetTypes.RANKINGS;
  metadata: RankingsMetadataInterface;
}

export interface RankingsV2Interface extends BaseWidgetInterface {
  column: ColumnTypes.SIDEBAR_1 | ColumnTypes.SIDEBAR_2;
  type: ColumnWidgetTypes.RANKINGS_V2;
  metadata: RankingsV2MetadataInterface;
}

export interface MLPLeaderboardInterface extends BaseWidgetInterface {
  column: ColumnTypes.SIDEBAR_1 | ColumnTypes.SIDEBAR_2;
  type: ColumnWidgetTypes.MLP_LEADERBOARD;
  metadata: MLPLeaderboardMetadataInterface;
}

// Represents side news widget that can be inside a column (widget with SIDE_NEWS type)
export interface SideNewsInterface extends BaseWidgetInterface {
  column: ColumnTypes.SIDEBAR_1 | ColumnTypes.SIDEBAR_2;
  type: ColumnWidgetTypes.SIDE_NEWS;
  metadata: SideNewsMetadataInterface;
}

// Represents side news widget that can be inside a column (widget with FEATURED_PLAYERS type)
export interface FeaturedPlayersInterface extends BaseWidgetInterface {
  column: ColumnTypes.SIDEBAR_1 | ColumnTypes.SIDEBAR_2;
  type: ColumnWidgetTypes.FEATURED_PLAYERS;
  metadata: FeaturedPlayersMetadataInterface;
}

// Represents social media widget that can be inside a column (widget with SOCIAL_MEDIA type)
export interface SocialMediaInterface extends BaseWidgetInterface {
  column: ColumnTypes.SIDEBAR_1 | ColumnTypes.SIDEBAR_2;
  type: ColumnWidgetTypes.SOCIAL_MEDIA;
  metadata: SocialMediaMetadataInterface;
}

// Represents side advertisement widget that can be inside a column (widget with SIDE_ADVERTISEMENT type)
export interface SideAdvertisementInterface extends BaseWidgetInterface {
  column: ColumnTypes.SIDEBAR_1 | ColumnTypes.SIDEBAR_2;
  type: ColumnWidgetTypes.SIDE_ADVERTISEMENT;
  metadata: SideAdvertisementMetadataInterface;
}

export interface MiddleAdvertisementInterface {
  uuid: string;
  type: ColumnWidgetTypes.MIDDLE_ADVERTISEMENT;
  metadata: MiddleAdvertisementMetadataInterface;
}

// Represents carousel widget that can be inside a column (widget with CAROUSEL type)
export interface CarouselWidgetInterface extends BaseWidgetInterface {
  column: ColumnTypes.CONTENT_AREA;
  type: ColumnWidgetTypes.CAROUSEL;
  metadata: CarouselInterface;
}

export interface AlertWidgetInterface extends BaseWidgetInterface {
  column: ColumnTypes.CONTENT_AREA;
  type: ColumnWidgetTypes.ALERT;
  metadata: AlertInterface;
}

export interface DefaultNewsletterMetadataInterface {
  variant: 'default';
}

export interface NewsletterWidgetInterface extends BaseWidgetInterface {
  column: ColumnTypes.SIDEBAR_1 | ColumnTypes.SIDEBAR_2;
  type: ColumnWidgetTypes.NEWSLETTER;
  metadata: DefaultNewsletterMetadataInterface;
}

export interface NewsletterError {
  statusCode?: number;
  error?: string;
}

export interface BlockMetadataInterace {
  blockHeader?: BlockHeaderInterface;
  blockComponents: BlockComponentType[];
  blockFooter?: BlockFooterInterface;
}

export interface NewsInterface {
  id: string;
  type: BlockWidgetTypes.NEWS;
  newsType: NewsFormatType;
  slug: string;
  prefetchedData?: RenderNewsInterface;
}

export interface VideoInterface {
  id: string;
  type: BlockWidgetTypes.VIDEO;
  featuredImageURL?: string;
  videoURL: string;
  title?: string;
}

export interface ImageInterface {
  id: string;
  type: BlockWidgetTypes.IMAGE;
  imageURL: string;
  imageAltText?: string;
  externalURL: string;
  description?: string;
}

export interface ExternalNewsInterface {
  id: string;
  type: BlockWidgetTypes.EXTERNAL_NEWS;
  newsType: NewsFormatType;
  url: string;
  title?: string;
  description?: string;
  image?: string;
  author?: string;
  datePublished?: string;
  prefetchedData?: RenderExternalNewsInterface;
}

export interface CompanyWebsitesMetadataInterface {
  title?: string;
  websites: WebsiteInterface[];
}

export interface FeaturedPartnersMetadataInterface {
  title?: string;
  partners: PartnerInterface[];
}

export interface HeadlinesMetadataInterface {
  data: unknown;
  prefetchedData?: HeadlineNewsInterface[];
}

type LeaderboardValue = 1 | 2 | 3 | 4 | 5;
type LeaderboardLabel = 'WD' | 'WS' | 'WX' | 'MX' | 'MD' | 'MS';
type LeaderboardGender = 'F' | 'M';
type LeaderboardSeconds = 5 | 10 | 15 | 30;

export interface ActualLeaderboardTabFullOption {
  value: LeaderboardValue;
  label: LeaderboardLabel;
  gender: LeaderboardGender;
}

export interface LeaderboardOption {
  tabValue: LeaderboardValue;
  tabLabel: LeaderboardLabel;
  tabGender: LeaderboardGender;
  changeTabs: boolean;
  secondsInBetweenChanges: LeaderboardSeconds;
}

export type LeaderboardV2Value = 'F' | 'M';
type LeaderboardV2Label = "WOMEN'S" | "MEN'S";

export interface LeaderboardV2Option {
  tabValue: LeaderboardV2Value;
  tabLabel: LeaderboardV2Label;
  changeTabs: boolean;
  secondsInBetweenChanges: LeaderboardSeconds;
}

export interface LeaderboardV2TabOption {
  value: LeaderboardV2Value;
  label: LeaderboardV2Label;
}

export interface MLPLeaderboardOption {
  changeTabs: boolean;
  secondsInBetweenChanges: LeaderboardSeconds;
}

export interface RankingsMetadataInterface {
  data: LeaderboardOption;
  prefetchedData?: WidgetPlayersRankingsData[];
}

export interface RankingsV2MetadataInterface {
  data: LeaderboardV2Option;
  prefetchedData?: WidgetPlayersRankingsV2Data[];
}

export interface MLPLeaderboardMetadataInterface {
  data: MLPLeaderboardOption;
  prefetchedData?: WidgetMLPLeaderboard;
}

export interface SideNewsMetadataInterface {
  title: string;
  sideNews: SideNewsRowInterface[];
  prefetchedData?: RenderSideNewsInterface[];
}

export interface SocialMediaMetadataInterface {
  title?: string;
  socialMedias: SocialMediaRowInterface[];
}

export type AdType = (typeof ZONE_IDS_PB)[keyof typeof ZONE_IDS_PB];
export interface SideAdvertisementMetadataInterface {
  placementId: FreestarPBPlacementId;
  zoneId: AdType;
  width: string;
  height: string;
  place?: string;
  prefetchedData?: PlacementInterface;
}

export interface CarouselInterface {
  id: string;
  type: BlockWidgetTypes.CAROUSEL;
  carouselNews: CarouselNewsInterface[];
  prefetchedData?: RenderSideNewsInterface[];
}

export interface MiddleAdvertisementMetadataInterface {
  placementId: FreestarPBPlacementId;
  zoneId: AdType;
  width: string;
  height: string;
  place: string;
  prefetchedData?: PlacementInterface;
}

export interface BlockHeaderInterface {
  headerType: BlockHeaderType;
  title: string;
  altTitle?: string;
  imageURL?: string;
  imageAltText?: string;
  link?: string;
  backgroundColor?: string;
}

export type BlockComponentType =
  | NewsInterface
  | VideoInterface
  | ImageInterface
  | ExternalNewsInterface
  | MatchCardsContainerInterface
  | ResultInterface
  | CarouselInterface
  | MatchV3Interface;

export interface BlockFooterInterface {
  buttons: ButtonInterface[];
}

export type NewsFormatType = 'format-a' | 'format-b';

export interface WebsiteInterface {
  id: string;
  name: string;
  link: string;
  image: string;
}

export interface PartnerInterface {
  id: string;
  name: string;
  link: string;
  image: string;
}

export type SocialMediaType =
  | 'Facebook'
  | 'Instagram'
  | 'Snapchat'
  | 'TikTok'
  | 'X/Twitter'
  | 'YouTube';

export interface SideNewsRowInterface {
  id: string;
  uuid: string;
  title: string;
}

export interface SocialMediaRowInterface {
  id: string;
  name: SocialMediaType;
  link: string;
}

export interface CarouselNewsInterface {
  id: string;
  uuid: string;
  title: string;
}

export interface ThemeInterface {
  backgroundColor: string;
  buttonBackgroundColor: string;
  buttonTextColor: string;
  textColor: string;
  linkColor: string;
  emojiBackgroundColor: string;
}

export type AlertTheme = 'purple' | 'white';

export interface AlertInterface {
  id: string;
  type: BlockWidgetTypes.ALERT;
  title: string;
  description: string;
  theme: AlertTheme;
  emoji?: string;
  linkUrl?: string;
  linkText?: string;
  links: LinkType[];
}

export interface LinkType {
  id: string;
  linkUrl?: string;
  linkText?: string;
}

// BlochHeader with headerType set to 'type-a' has an image on the start (left) and a title just next to it
// BlockHeader with headerType set to 'type-b' has an image at the end (all the way on the right) and a title on the start (left)
// BlochHeader with headerType set to 'type-c' doesn't have an image, but has in the middle a text with a custom background color which can be a link
export type BlockHeaderType = 'type-a' | 'type-b' | 'type-c';

export interface MatchCardsContainerInterface {
  type: BlockWidgetTypes.MATCH;
  id: string;
  matches: MatchInterface[];
  title: string;
  invisible?: boolean;
  prefetchedData?: MatchInfo[];
}

export interface MatchInterface {
  id: string;
  tournamentId: string;
  eventId: string;
  matchId: string;
  matchTitle: string;
}

export interface ResultInterface {
  type: BlockWidgetTypes.RESULT;
  id: string;
  resultInfo: MatchInterface;
  invisible?: boolean;
  prefetchedData?: MatchInfo[];
}

export interface MatchV3Interface {
  type: BlockWidgetTypes.MATCHV3;
  id: string;
  match: MatchInterface;
  invisible?: boolean;
  prefetchedData?: MatchInfo[];
}

// For a Button 'text' property represent what text is inside of a Button and a link defines which URL opens when you click the Button
export interface ButtonInterface {
  id: string;
  text: string;
  link: string;
  isBetting?: boolean;
  bettingSite?: string;
}

export interface NewsDetailsInterface {
  uuid: string;
  title: string;
  imageUrl: string;
  imageAltText: string;
  authorFullName: string;
  dateCreated: Date | string;
  facebookLink: string;
  twitterLink: string;
  smsLink: string;
  videoUrl: string;
  content: string;
  newsTagsUuids: string[];
  dateArchived: Date | string;
  dateStartShowOnSite: Date | string;
  dateEndShowOnSite: Date | string;
  publishDateDisplayed: Date | string;
  smallNewsCardImgUrl: string;
  headlineForHomepage: string;
  dateUpdated: Date | string;
  createdByFullName: string;
  updatedByFullName: string;
  archivedByFullName: string;
  description: string;
  detailsUrl: string;
  slug: string;
  status: string;
  newsAuthorUuid: string;
  externalUrl: string;
  dateStartShowOnFeatured: Date | string;
  dateEndShowOnFeatured: Date | string;
  tabTitle: string;
  metaTitle: string;
  metaDescription: string;
  imageCaption: string;
  imageSource: string;
  isActive: boolean;
  categoryUuid: string;
  subcategoryUuids: string[];
  newsCategory: NewsCategoryInterface;
  isBetting?: boolean;
  bettingSite?: string;
}

export interface NewsCategoryInterface {
  uuid: string;
  title: string;
  slug: string;
  description: string;
  isActive: boolean;
  dateCreated: string;
  dateUpdated: string;
  userIdCreated: string;
  userIdUpdated: string;
  articleCount: string;
  subcategories: Subcategory[];
}

export interface NewsCategoryShortInterface {
  uuid: string;
  title: string;
  slug: string;
}

export interface HeadlineNewsInterface {
  uuid: string;
  slug: string;
  title: string;
  headlineForHomepage: string;
  image: string;
  visibleFrom?: Date;
  visibleTo?: Date;
  status: string;
  categorySlug?: string;
  sortOrder: number;
}

export interface HeadlineState {
  structure: HeadlineNewsInterface[];
  maxPinned: number;
}

export interface MasterDBV2Response<T> {
  totalRecords: number;
  currentPage: number;
  pageSize: number;
  nextPage: string;
  prevPage: string;
  results: T;
}

export interface NewsResponse {
  newsArticles: NewsDetailsInterface[];
}

export interface FavoritedNewsResponse {
  modules: FavoritedNewsModule[];
}

export interface FavoritedNewsModule {
  moduleFor: string;
  moduleUuid: string;
  userUuid: string;
  dateCreated: string;
  dateDisabled: string;
  moduleData: NewsDetailsInterface;
  isOrganizationMemberExpired: boolean;
  organizationMember: string;
}

export interface RenderFavoritedNewsModule {
  moduleUuid: string;
  title: string;
  imageUrl: string;
  imageAltText: string;
  publishDateDisplayed: string;
  slug: string;
  description: string;
  newsCategorySlug: string;
}

export interface NewsCategoriesResponse {
  categories: NewsCategoryInterface[];
}

export interface FeaturedPlayersMetadataInterface {
  title: string;
  players: FeaturedPlayerRowInterface[];
  prefetchedData?: PlayerResponse[];
  linkUrl?: string;
  linkText?: string;
}

export interface FeaturedPlayerRowInterface {
  id: string;
  uuid: string;
  name: string;
  slug: string;
}

export interface PlayerResponse {
  countryAbbreviation: string;
  countryAbbreviation2Digit: string;
  firstName: string;
  lastName: string;
  middleName: string;
  nameSuffix: string;
  userImages: {
    type: string;
    url: string;
  }[];
  representingCountryAbbreviation: string;
  representingCountryAbbreviation2Digit: string;
  showMiddleNameToPublic: boolean;
  showOnlyFirstInitialOfLastNameToPublic: boolean;
  single365Points: number;
  doubles365Points: number;
  single365Rank: string;
  doubles365Rank: string;
  slug: string;
  uuid: string;
  sponsors: {
    isPrimary: boolean;
    sponsorImage: string;
    sponsorName: string;
    sponsorUrl: string;
    imageSize: string;
    imageColor: string;
  }[];
}

export interface NotificationLookup {
  isBalanceDue: boolean;
  isWaiverDue: boolean;
  isBalanceDuePbb: boolean;
  isWaiverDuePbb: boolean;
  isBalanceDueTl: boolean;
}
